import apiClient from "./api";

export interface ServerConfig {
  id?: string;
  name: string;
  description: string;
  version?: string;
  baseUrl?: string;
  authType?: string;
  authConfig?: string;
  createdAt?: string;
  updatedAt?: string;
  isActive?: boolean;
  processingProgress?: number;
  // Detailed processing counts
  processedModels?: number;
  totalModels?: number;
  processedTools?: number;
  totalTools?: number;
  enabledTools?: number;
  // Authorization fields (server-level)
  authorizationType?: string;
  staticAuthHeaders?: string;
  dynamicAuthHeaders?: string;
  allowAuthUpdate?: boolean;
  authInstructionsUrl?: string;
  // CustomAuthEndpoint fields
  autoTokenEndpoint?: string;
  autoTokenMethod?: string;
  autoTokenRequestHeaders?: string;
  autoTokenRequestBody?: string;
  autoTokenRequestPlaceholders?: string;
  autoTokenResponseExample?: string;
  autoTokenResponsePath?: string;
  autoTokenHeaderName?: string;
  autoTokenHeaderFormat?: string;
  autoTokenSecret?: string;
  // Refresh token fields
  refreshTokenPath?: string;
  refreshTokenEndpoint?: string;
  refreshTokenMethod?: string;
  refreshTokenRequestHeaders?: string;
  refreshTokenRequestBody?: string;
  refreshTokenResponsePath?: string;
  /**
   * URL the toolkit's API specification is fetched from. Returned by single-server reads
   * (`getServerById`, `updateServer`, `updateSpecUrl`) — not by the server list.
   */
  specUrl?: string;
  /** Documentation format the toolkit was generated from. */
  apiDocType?: ApiDocType;
  // AI capabilities summary
  capabilitiesSummary?: string;
  // True while the capabilities summary is stale: set by any tool change, cleared by the automatic
  // rebuild a few seconds later. Stays true only if that rebuild failed.
  capabilitiesRegenerateNeeded?: boolean;
}

export enum ApiDocType {
  Swagger = 0,
  Readme = 1,
}

export interface CreateServerRequest {
  name: string;
  description: string;
  specUrl: string;
  additionalDetailsUrl?: string;
  apiConfig?: any;
  apiDocType?: ApiDocType;
  autoGeneratePrompts?: boolean;
  isOnboarding?: boolean;
}

export interface CreateServerResponse {
  id: string;
  name: string;
  toolCount: number;
  resourceCount: number;
  promptCount: number;
  message: string;
}

export interface ValidateSpecRequest {
  specUrl: string;
  apiDocType: ApiDocType;
}

export interface ValidateSpecResponse {
  isValid: boolean;
  message: string;
  serverName?: string;
  serverDescription?: string;
  estimatedToolCount?: number;
  /**
   * The spec URL that was actually read. Differs from the submitted URL when a documentation
   * page (e.g. a Swagger UI page) was submitted and the real spec URL was resolved from it.
   * Submit this value when creating the toolkit.
   */
  resolvedSpecUrl?: string;
  /** True when the submitted URL was a documentation page and `resolvedSpecUrl` was discovered from it. */
  specUrlWasResolved?: boolean;
}

export interface UpdateSpecUrlRequest {
  specUrl: string;
}

export interface UpdateSpecUrlResponse {
  serverId: string;
  /** The URL now stored — the resolved spec URL when a documentation page was submitted. */
  specUrl: string;
  specUrlWasResolved: boolean;
  /** False when the resolved URL matched the stored one and nothing was written. */
  changed: boolean;
  estimatedToolCount: number;
  message: string;
}

export interface BulkEnableToolsRequest {
  toolIds: string[];
}

export interface BulkEnableToolsResponse {
  enabledCount: number;
  failedIds: string[];
}

export enum ChangeType {
  New = 0,
  Updated = 1,
  Deleted = 2,
}

export interface ParameterInfo {
  name: string;
  in: string; // path, query, header
  type: string;
  required: boolean;
  description: string;
}

export interface SchemaInfo {
  type: string;
  description: string;
  jsonSchema?: string;
}

export interface EndpointDetails {
  summary: string;
  description: string;
  parameters: ParameterInfo[];
  requestBody?: SchemaInfo;
  responses: Record<string, SchemaInfo>;
}

export interface EndpointChange {
  path: string;
  method: string;
  changeType: ChangeType;
  existingToolId?: string;
  oldDetails?: EndpointDetails;
  newDetails?: EndpointDetails;
}

export interface CompareDocumentationResponse {
  hasChanges: boolean;
  currentSpecVersion: string;
  newSpecVersion: string;
  changes: EndpointChange[];
}

export interface SelectedEndpointChange {
  path: string;
  method: string;
  changeType: number;
  existingToolId?: string;
}

export interface ApplyDocumentationUpdatesRequest {
  selectedChanges: SelectedEndpointChange[];
}

export interface ApplyDocumentationUpdatesResponse {
  message: string;
  updateId: string;
  selectedChangesCount: number;
}

export const serverService = {
  getAllServers: async (): Promise<ServerConfig[]> => {
    const response = await apiClient.get("/servers");
    return response.data;
  },

  getServerById: async (id: string): Promise<ServerConfig> => {
    const response = await apiClient.get(`/servers/${id}`);
    return response.data;
  },

  createServer: async (
    data: CreateServerRequest,
  ): Promise<CreateServerResponse> => {
    const response = await apiClient.post("/servers/generate", data);
    return response.data;
  },

  updateServer: async (
    id: string,
    data: Partial<ServerConfig>,
  ): Promise<ServerConfig> => {
    const response = await apiClient.put(`/servers/${id}`, data);
    return response.data;
  },

  deleteServer: async (id: string): Promise<void> => {
    await apiClient.delete(`/servers/${id}`);
  },

  toggleServerStatus: async (
    id: string,
    isActive: boolean,
  ): Promise<ServerConfig> => {
    const response = await apiClient.patch(`/servers/${id}/status`, {
      isActive,
    });
    return response.data;
  },

  validateSpec: async (
    data: ValidateSpecRequest,
  ): Promise<ValidateSpecResponse> => {
    const response = await apiClient.post("/servers/validate-spec", data);
    return response.data;
  },

  updateSpecUrl: async (
    id: string,
    data: UpdateSpecUrlRequest,
  ): Promise<UpdateSpecUrlResponse> => {
    const response = await apiClient.put(`/servers/${id}/spec-url`, data);
    return response.data;
  },

  bulkEnableTools: async (
    serverId: string,
    toolIds: string[],
  ): Promise<BulkEnableToolsResponse> => {
    const response = await apiClient.patch(
      `/servers/${serverId}/tools/bulk-enable`,
      { toolIds },
    );
    return response.data;
  },

  compareDocumentation: async (
    serverId: string,
  ): Promise<CompareDocumentationResponse> => {
    const response = await apiClient.post(
      `/servers/${serverId}/compare-documentation`,
    );
    return response.data;
  },

  applyDocumentationUpdates: async (
    serverId: string,
    request: ApplyDocumentationUpdatesRequest,
  ): Promise<ApplyDocumentationUpdatesResponse> => {
    const response = await apiClient.post(
      `/servers/${serverId}/apply-updates`,
      request,
    );
    return response.data;
  },

  copyServer: async (
    id: string,
  ): Promise<ServerConfig & { copiedWidgetsCount: number }> => {
    const response = await apiClient.post(`/servers/${id}/copy`);
    return response.data;
  },

  regenerateCapabilities: async (id: string): Promise<ServerConfig> => {
    const response = await apiClient.post(
      `/servers/${id}/regenerate-capabilities`,
    );
    return response.data;
  },
};
